import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Check, X, Crown, Shield, Sparkles, ArrowLeft, Loader2,
  ShoppingCart, Building2, Award, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PricingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [addingToCart, setAddingToCart] = useState(null);
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const plansRes = await axios.get(`${API_URL}/api/subscription-plans/public`);
      setSubscriptionPlans(plansRes.data);
      
      // Get cart count if logged in
      if (user) {
        try {
          const cartRes = await axios.get(`${API_URL}/api/organizer/cart`);
          setCartCount(cartRes.data.items?.length || 0);
        } catch (e) {
          // Not logged in or no cart
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (plan) => {
    if (plan.price === 0) {
      navigate('/organizer');
      return;
    }

    if (!user) {
      toast.error('Please login to add to cart');
      navigate('/organizer/login');
      return;
    }

    setAddingToCart(plan.id);
    try {
      await axios.post(`${API_URL}/api/organizer/cart/add?plan_id=${plan.id}`);
      toast.success(`${plan.name} added to cart`);
      setCartCount(prev => prev + 1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to cart');
    } finally {
      setAddingToCart(null);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === 0) return 'FREE';
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getPlanIcon = (tier) => {
    switch (tier) {
      case 'basic': return <Building2 className="h-8 w-8" />;
      case 'organized': return <Crown className="h-8 w-8" />;
      case 'transparency': return <Shield className="h-8 w-8" />;
      case 'license': return <Award className="h-8 w-8" />;
      default: return <Zap className="h-8 w-8" />;
    }
  };

  const getPlanColors = (tier, isPopular) => {
    if (isPopular) {
      return {
        border: 'border-blue-400 ring-2 ring-blue-500 ring-offset-2',
        bg: 'bg-gradient-to-b from-blue-50 to-white',
        icon: 'bg-blue-100 text-blue-600',
        price: 'text-blue-600',
        button: 'bg-blue-600 hover:bg-blue-700 text-white',
        badge: 'bg-blue-500'
      };
    }
    
    switch (tier) {
      case 'basic':
        return {
          border: 'border-green-300',
          bg: 'bg-gradient-to-b from-green-50 to-white',
          icon: 'bg-green-100 text-green-600',
          price: 'text-green-600',
          button: 'bg-green-600 hover:bg-green-700 text-white',
          badge: 'bg-green-500'
        };
      case 'transparency':
        return {
          border: 'border-purple-300',
          bg: 'bg-gradient-to-b from-purple-50 to-white',
          icon: 'bg-purple-100 text-purple-600',
          price: 'text-purple-600',
          button: 'bg-purple-600 hover:bg-purple-700 text-white',
          badge: 'bg-purple-500'
        };
      case 'license':
        return {
          border: 'border-orange-300',
          bg: 'bg-gradient-to-b from-orange-50 to-white',
          icon: 'bg-orange-100 text-orange-600',
          price: 'text-orange-600',
          button: 'bg-orange-600 hover:bg-orange-700 text-white',
          badge: 'bg-orange-500'
        };
      default:
        return {
          border: 'border-gray-300',
          bg: 'bg-white',
          icon: 'bg-gray-100 text-gray-600',
          price: 'text-gray-900',
          button: 'bg-gray-800 hover:bg-gray-900 text-white',
          badge: 'bg-gray-500'
        };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-cream to-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-maroon" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-cream to-white">
      {/* Header */}
      <div className="bg-maroon text-white py-4 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <span className="font-heading text-xl">batchm8s</span>
          {user && (
            <Button 
              variant="ghost"
              onClick={() => navigate('/organizer/cart')}
              className="text-white hover:bg-white/10 relative"
            >
              <ShoppingCart className="h-5 w-5" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-gold text-maroon text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                  {cartCount}
                </span>
              )}
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Title */}
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-maroon mb-4">
            Choose Your Reunion Plan
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Simple pricing. One-time payment per reunion.<br />
            <span className="font-semibold">No subscriptions. No hidden fees.</span>
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {subscriptionPlans.map((plan) => {
            const colors = getPlanColors(plan.tier, plan.is_popular);
            
            return (
              <Card 
                key={plan.id}
                className={`relative border-2 transition-all hover:shadow-xl ${colors.border} ${colors.bg}`}
              >
                {plan.is_popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className={`${colors.badge} text-white px-4 py-1`}>
                      <Sparkles className="h-3 w-3 mr-1" />
                      MOST POPULAR
                    </Badge>
                  </div>
                )}
                
                <CardHeader className="text-center pt-8 pb-4">
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${colors.icon}`}>
                    {getPlanIcon(plan.tier)}
                  </div>
                  <div className="flex items-center justify-center gap-2 mb-1">
                    {plan.tier === 'basic' && <span className="text-green-600 text-2xl">🟢</span>}
                    {plan.tier === 'organized' && <span className="text-blue-600 text-2xl">🔵</span>}
                    {plan.tier === 'transparency' && <span className="text-purple-600 text-2xl">🟣</span>}
                    {plan.tier === 'license' && <span className="text-orange-600 text-2xl">🟠</span>}
                    <CardTitle className="font-heading text-xl">{plan.name}</CardTitle>
                  </div>
                  <CardDescription className="text-gray-600 text-sm">
                    {plan.tagline}
                  </CardDescription>
                  <div className="mt-4">
                    <span className={`text-4xl font-bold ${colors.price}`}>
                      {formatCurrency(plan.price)}
                    </span>
                    <span className="text-gray-500 text-sm block mt-1">
                      {plan.price_type === 'yearly' ? 'Yearly' : 'One-time per reunion'}
                    </span>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4">
                  {/* Included Features */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Includes</p>
                    <ul className="space-y-2">
                      {plan.features_included?.slice(0, 6).map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <Check className={`h-4 w-4 flex-shrink-0 mt-0.5 ${colors.price}`} />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                      {plan.features_included?.length > 6 && (
                        <li className="text-xs text-gray-500 pl-6">
                          +{plan.features_included.length - 6} more features
                        </li>
                      )}
                    </ul>
                  </div>

                  {/* Excluded Features (for Basic plan) */}
                  {plan.features_excluded?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Not included</p>
                      <ul className="space-y-1">
                        {plan.features_excluded.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-400">
                            <X className="h-4 w-4 flex-shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Best For */}
                  <div className="pt-2 border-t">
                    <p className="text-xs text-gray-500">
                      <span className="font-semibold">Best for:</span> {plan.best_for}
                    </p>
                  </div>

                  <Button
                    onClick={() => handleAddToCart(plan)}
                    disabled={addingToCart === plan.id}
                    className={`w-full mt-4 ${colors.button}`}
                    data-testid={`select-plan-${plan.id}`}
                  >
                    {addingToCart === plan.id ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : plan.price === 0 ? null : (
                      <ShoppingCart className="h-4 w-4 mr-2" />
                    )}
                    {plan.price === 0 ? 'Start Free' : `Add to Cart`}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Why batchm8s Section */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border mb-8">
          <h2 className="font-heading text-2xl font-bold text-maroon mb-6 text-center">
            Why batchm8s?
          </h2>
          <div className="grid md:grid-cols-5 gap-4 text-center">
            <div className="p-4">
              <Check className="h-6 w-6 mx-auto mb-2 text-green-500" />
              <p className="text-sm text-gray-700">Designed for school reunions</p>
            </div>
            <div className="p-4">
              <Check className="h-6 w-6 mx-auto mb-2 text-green-500" />
              <p className="text-sm text-gray-700">Supports multiple payment methods</p>
            </div>
            <div className="p-4">
              <Check className="h-6 w-6 mx-auto mb-2 text-green-500" />
              <p className="text-sm text-gray-700">Built for transparency and accountability</p>
            </div>
            <div className="p-4">
              <Check className="h-6 w-6 mx-auto mb-2 text-green-500" />
              <p className="text-sm text-gray-700">No recurring charges for reunions</p>
            </div>
            <div className="p-4">
              <Check className="h-6 w-6 mx-auto mb-2 text-green-500" />
              <p className="text-sm text-gray-700">One official workspace for your batch</p>
            </div>
          </div>
        </div>

        {/* Important Notes */}
        <div className="bg-cream-paper rounded-xl p-6 text-center">
          <h3 className="font-semibold text-maroon mb-3">Important Notes</h3>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• All reunion plans are one-time access, valid until the reunion concludes</li>
            <li>• No automatic renewals</li>
            <li>• No hidden fees</li>
          </ul>
          <p className="mt-4 text-maroon font-medium">
            Ready to organize your reunion properly?<br />
            <span className="text-gray-600 font-normal">Start free. Upgrade only when you're ready.</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
